/**
 * The app whose blog shows a post. A post names one or both; the athlete app's copy is canonical when it has both.
 * A post is written in the platform's `APP_LANGUAGES` (`@enums/languages`), one translation each.
 */
export enum BlogAudience {
  ATHLETE = "athlete",
  COACH = "coach",
}

/** Where a post's translation is in its life. A published translation with a future `publishedAt` is scheduled: readers see it from then on. */
export enum BlogPostStatus {
  DRAFT = "draft",
  PUBLISHED = "published",
}

/** Who a post is signed by: one person, or the whole company ("Team Complete Athlete"). */
export enum BlogAuthorKind {
  PERSON = "person",
  TEAM = "team",
}

/** The reactions a signed-in reader can leave under a post, one of each at most. */
export enum BlogReaction {
  FIRE = "fire",
  STRONG = "strong",
  HEART = "heart",
  CLAP = "clap",
  MIND_BLOWN = "mind_blown",
}

export const BLOG_REACTIONS = Object.values(BlogReaction);

export const BLOG_REACTION_EMOJI: Record<BlogReaction, string> = {
  [BlogReaction.FIRE]: "🔥",
  [BlogReaction.STRONG]: "💪",
  [BlogReaction.HEART]: "❤️",
  [BlogReaction.CLAP]: "👏",
  [BlogReaction.MIND_BLOWN]: "🤯",
};

/** Words a reader gets through in a minute of an article, for the "6 min read" under a title. */
export const BLOG_WORDS_PER_MINUTE = 220;

/** Minutes a Markdown body takes to read, at least one. Images and code count as nothing. */
export const blogReadingMinutes = (markdown: string): number => {
  const text = markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[#>*_`~|-]/g, " ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / BLOG_WORDS_PER_MINUTE));
};

/** A URL-safe slug from a title: lowercase ASCII words joined by dashes, Polish letters folded. */
export const blogSlugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/ł/g, "l")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96)
    .replace(/-+$/g, "");
